import { Formik, Form } from 'formik';

import { useUsers } from '@/portainer/users/queries';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';

import { FormActions } from '@@/form-components/FormActions';

import { KubernetesRbacFormData } from './types';
import { KubernetesRBACInnerForm } from './KubernetesRBACInnerForm';

interface Props {
  initialValues: KubernetesRbacFormData;
  onSubmit: (values: KubernetesRbacFormData) => void | Promise<void>;
  isSubmitting?: boolean;
  /** Mode: 'create' | 'edit' */
  mode: 'create' | 'edit';
  /** Whether the policy name has been provided (for validation) */
  nameProvided: boolean;
  /** Whether the environment groups have been provided (for validation) */
  environmentGroupsProvided: boolean;
}

/**
 * Kubernetes RBAC Policy Form Component
 *
 * Renders the access management form for Kubernetes RBAC policies.
 * Can be used for both creating new policies and editing existing ones.
 */
export function KubernetesRBACPolicyForm({
  initialValues,
  onSubmit,
  isSubmitting = false,
  mode,
  nameProvided,
  environmentGroupsProvided,
}: Props) {
  // Fetch users, teams, and roles for the access management form
  const userOptionsQuery = useUsers(false, undefined, true, (users) =>
    users.map((user) => ({ value: user.Id, label: user.Username }))
  );
  const teamOptionsQuery = useTeams(false, undefined, {
    select: (teams) => teams.map((t) => ({ value: t.Id, label: t.Name })),
  });
  const roleOptionsQuery = useRbacRoles({
    select: (roles) =>
      roles
        .filter((role) => role.Scope && role.Scope.kubernetes)
        .map((role) => ({ value: role.Id, label: role.Name })),
  });

  return (
    <Formik<KubernetesRbacFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ values, setFieldValue, isValid }) => (
        <Form>
          <KubernetesRBACInnerForm
            formData={values}
            onChange={(key, value) => setFieldValue(key, value)}
            userOptions={userOptionsQuery.data || []}
            teamOptions={teamOptionsQuery.data || []}
            roleOptions={roleOptionsQuery.data || []}
          />

          <FormActions
            submitLabel={mode === 'create' ? 'Create policy' : 'Update policy'}
            loadingText={mode === 'create' ? 'Creating...' : 'Updating...'}
            isLoading={isSubmitting}
            isValid={
              isValid &&
              !!nameProvided &&
              !!environmentGroupsProvided &&
              (values.accessList?.length ?? 0) > 0
            }
            data-cy="policy-form-actions"
          />
        </Form>
      )}
    </Formik>
  );
}
